'use client';

import { ReactNode, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useRafState } from 'react-use';

import { Styles, css } from '@flipster/styled-system/css';
import { scrollbar } from '@flipster/styled-system/recipes';

import { useIntersection } from '../misc/browser/intersection';

export interface VirtualScrollRef {
  scrollTo: (x: number, y: number) => void;
}

export interface VirtualScrollProps<I> {
  isLoading?: boolean;
  items: I[];
  itemHeight: number;
  renderItem: (item: I, index: number) => ReactNode;
  keyExtractor: (item: I, index: number) => string;
  totalItemCount?: number;
  overscan?: number;
  gap?: number;
  emptyPlaceholder?: ReactNode;
  onScrollEnd?: () => void;
  css?: Styles;
  ref?: Ref<VirtualScrollRef>;
}

/**
 * 고정 높이 아이템을 위한 가상화 스크롤 컴포넌트.
 * 보이는 영역의 아이템만 DOM에 렌더링하여 대량 데이터에서도 일정한 성능을 유지합니다.
 *
 * @template I - 아이템 타입
 *
 * @param props.itemHeight - 각 아이템의 고정 높이 (px). 가상화 계산의 기준값.
 * @param props.items - 렌더링할 아이템 배열
 * @param props.renderItem - 아이템을 ReactNode로 변환하는 렌더 함수
 * @param props.keyExtractor - 각 아이템의 고유 key를 반환하는 함수 (React reconciliation용)
 * @param props.overscan - 뷰포트 위아래로 미리 렌더링할 추가 아이템 수. 빠른 스크롤 시 빈 화면 노출 방지. @default 2
 * @param props.totalItemCount - 가상화 높이 계산용 전체 개수. 무한스크롤에서 서버 총 개수를 알 때 사용하면 스크롤바 비율이 정확해짐. @default items.length
 * @param props.gap - 아이템 사이 간격 (px) @default 0
 * @param props.isLoading - `true`이면 아이템을 렌더링하지 않음. 로딩 중 스켈레톤을 외부에서 처리할 때 사용. @default false
 * @param props.emptyPlaceholder - `items`가 비어있고 `isLoading`이 `false`일 때 표시할 ReactNode
 * @param props.onScrollEnd - 스크롤이 하단에 도달했을 때 호출되는 콜백. IntersectionObserver 기반으로 동작하여 반복 발화 없음.
 * @param props.css - Panda CSS 스타일 오버라이드
 * @param props.ref - `scrollTo` 메서드에 접근하기 위한 ref
 *
 * @example
 * ```tsx
 * // flex 컬럼 레이아웃에서 남은 공간을 채우는 경우
 * // VirtualScroll에 minHeight: 0이 내장되어 있으므로 wrapper div 불필요
 * <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
 *   <Header />
 *   <VirtualScroll
 *     css={{ flex: 1 }}
 *     items={orders}
 *     itemHeight={56}
 *     gap={8}
 *     renderItem={(item) => <OrderRow order={item} />}
 *     keyExtractor={(item) => item.id}
 *     totalItemCount={serverTotal}
 *     onScrollEnd={fetchNextPage}
 *     emptyPlaceholder={<EmptyState />}
 *   />
 * </div>
 * ```
 */
export function VirtualScroll<I>({
  isLoading,
  itemHeight,
  items,
  renderItem,
  keyExtractor,
  overscan = 2,
  totalItemCount,
  gap = 0,
  emptyPlaceholder,
  onScrollEnd,
  css: cssProps,
  ref,
}: VirtualScrollProps<I>) {
  const [contentHeight, setContentHeight] = useState(0);
  const [containerRef, scrollTop] = useVirtualScroll();
  const sentinelRef = useScrollEnd(containerRef, onScrollEnd);

  const count = totalItemCount ?? items.length;
  const visibleItemHeight = itemHeight + gap;
  const realTotalHeight = visibleItemHeight * count - gap;
  const startNode = Math.max(0, Math.floor(scrollTop / visibleItemHeight) - overscan);
  const visibleNodeCount = Math.min(count - startNode, Math.ceil(contentHeight / visibleItemHeight) + overscan * 2);
  const offsetY = startNode * visibleItemHeight;

  useImperativeHandle(ref, () => ({
    scrollTo(x: number, y: number) {
      containerRef.current?.scrollTo(x, y);
    },
  }));

  useEffect(() => {
    const container = containerRef.current;
    if (container == null) return;

    const observer = new ResizeObserver(([entry]) => {
      setContentHeight(entry.contentRect.height);
    });
    observer.observe(container);
    return () => observer.unobserve(container);
  }, [containerRef]);

  return (
    <div
      className={css(
        { overflowY: 'auto', overscrollBehavior: 'contain', h: '100%', minH: 0 },
        scrollbar.raw(),
        cssProps,
      )}
      ref={containerRef}
    >
      {!isLoading && items.length === 0 && emptyPlaceholder}
      {!isLoading && items.length > 0 && (
        <div className={css({ position: 'relative', overflowY: 'hidden' })} style={{ height: realTotalHeight }}>
          <ul className={css({ willChange: 'transform' })} style={{ transform: `translateY(${offsetY}px)` }}>
            {items.slice(startNode, startNode + visibleNodeCount).map((item, sliceIndex) => {
              const index = startNode + sliceIndex;
              return (
                <li key={keyExtractor(item, index)} style={{ height: itemHeight }}>
                  {renderItem(item, index)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <div ref={sentinelRef} />
    </div>
  );
}

function useVirtualScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useRafState<number>(0);

  useEffect(() => {
    const divEl = ref.current;
    if (!divEl) return;

    const scrollListener = () => setScrollTop(divEl.scrollTop);

    setScrollTop(divEl.scrollTop);
    divEl.addEventListener('scroll', scrollListener, { passive: true });
    return () => divEl.removeEventListener('scroll', scrollListener);
  }, [setScrollTop]);

  return [ref, scrollTop] as const;
}

function useScrollEnd(containerRef: React.RefObject<HTMLDivElement | null>, onScrollEnd?: () => void) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onScrollEnd);

  useEffect(() => {
    callbackRef.current = onScrollEnd;
  });

  useIntersection(
    sentinelRef,
    ([entry]) => {
      if (entry.isIntersecting) callbackRef.current?.();
    },
    { root: containerRef.current, threshold: 0 },
  );

  return sentinelRef;
}
